import { get } from 'lodash';

/**
 * Resolves a receipt field value from a sale object using a declarative config.
 *
 * Replaces the arrow functions that were previously inlined in tenantConfig.js.
 * Each receipt field item in config.json now has a declarative shape instead of a `method` function:
 *
 *   { "label": "Titular de tarjeta", "fieldPath": "payment.cardHolderName" }
 *   { "label": "Tasa comisión", "fieldPath": "commission", "suffix": "%" }
 *   { "label": "Modo de entrada", "type": "inputModeLabel" }
 *   { "label": "Terminal", "type": "terminalTypeOrDash" }
 *
 * For the default config's complex methods (saleInformation date, map, recharge, items, contactData),
 * those stay as-is in customConfig.js since they are shared across all tenants and not overridden per-tenant.
 */

const NAMED_RESOLVERS = {
  // vendemas: maps inputMode to human-readable label
  inputModeLabel: (sale) => {
    const map = {
      EMV: 'Chip',
      NFC: 'Sin Contacto',
      SWIPE: 'Banda',
      NFC_PHONE: 'Tap on Phone',
      ECOMMERCE: 'Link',
    };
    return map[sale.inputMode] || sale.inputMode;
  },

  // vendemas: shows terminal type or dash for Tap on Phone / Link
  terminalTypeOrDash: (sale) => {
    const type = sale.terminal?.type;
    return (type === 'Tap on Phone' || type === 'Link') ? '-' : type;
  },

  // vendemas: QR tag ID
  qrTagId: (sale) => (sale.qr && sale.qr.tagId) || null,

  // vendemas: QR customer name
  qrCustomerName: (sale) => (sale.qr && sale.qr.customerName) || null,

  // payscan: always returns false (hides contact data)
  alwaysFalse: () => false,
};

/**
 * Resolves a single receipt field item's value from a sale object.
 *
 * @param {object} item - The declarative config item (from config.json)
 * @param {object} sale - The sale/transaction object
 * @returns {*} The resolved value
 */
export const resolveReceiptField = (item, sale) => {
  // Legacy support: if item still has a `method` function, call it directly
  if (typeof item.method === 'function') {
    return item.method(sale);
  }

  // Named resolver (for complex/conditional logic)
  if (item.type && NAMED_RESOLVERS[item.type]) {
    return NAMED_RESOLVERS[item.type](sale);
  }

  // Field path resolver (most common case)
  if (item.fieldPath) {
    const value = get(sale, item.fieldPath);
    if (value == null) return null;
    return item.suffix ? value + item.suffix : value;
  }

  return null;
};

/**
 * Resolves an extraItems value accessor.
 * In JSON config: { "taxAmount": { "label": "Impuesto", "fieldPath": "taxAmount" } }
 *
 * @param {object} extraItemConfig - The extra item config
 * @param {object} sale - The sale/transaction object
 * @returns {*} The resolved value
 */
export const resolveExtraItemValue = (extraItemConfig, sale) => {
  // Legacy support
  if (typeof extraItemConfig.value === 'function') {
    return extraItemConfig.value(sale);
  }

  if (extraItemConfig.fieldPath) {
    return get(sale, extraItemConfig.fieldPath);
  }

  return null;
};
